<template>
  <div class="clipr-purchases-page" :class="{ 'twitch-theme': isTwitchUser }">
    <card class="card clipr-purchases-card mb-4">
      <div slot="header" class="d-flex flex-wrap align-items-center justify-content-between">
        <div>
          <h4 class="card-title mb-1">Clipr Purchases (Admin)</h4>
        </div>
        <div class="d-flex flex-wrap align-items-center mt-2 mt-md-0">
          <label class="mb-0 mr-2 text-muted small">Límite</label>
          <select v-model.number="limit" class="form-control form-control-sm mr-2 limit-select">
            <option :value="25">25</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
            <option :value="200">200</option>
          </select>
          <label class="mb-0 mr-2 d-flex align-items-center clipr-welcome-bonus-check">
            <input v-model="showWelcomeBonus" type="checkbox" class="mr-1" />
            <span class="small">Mostrar welcome_bonus</span>
          </label>
          <button class="btn btn-primary btn-sm btn-round" :disabled="loading" @click="loadTables">
            <i :class="{ 'ti-spin': loading }" class="ti-reload mr-1"></i>
            {{ loading ? "Cargando..." : "Recargar" }}
          </button>
        </div>
      </div>

      <div slot="raw-content" class="clipr-purchases-body">
        <div v-if="errorMessage" class="alert alert-danger">
          {{ errorMessage }}
        </div>

        <div v-for="section in tableSections" :key="section.key" class="clipr-table-section mb-4">
          <div class="clipr-table-header d-flex align-items-center justify-content-between">
            <strong class="clipr-table-title">{{ section.label }}</strong>
            <div class="d-flex align-items-center">
              <button
                class="btn btn-outline-secondary btn-sm mr-2"
                :disabled="!section.hasActiveFilters"
                @click="clearSectionFilters(section.key)"
              >
                Limpiar filtros
              </button>
              <span class="badge badge-secondary">
                {{ section.filteredRows.length }} / {{ section.poolRows.length }} filas
              </span>
            </div>
          </div>
          <div v-if="section.apiRows.length === 0" class="clipr-table-empty">
            <i class="ti-folder"></i>
            <p>Sin datos.</p>
          </div>
          <div v-else class="clipr-table-responsive">
            <table class="table table-hover clipr-data-table mb-0">
              <thead>
                <tr>
                  <th
                    v-for="column in section.columns"
                    :key="column"
                    :class="[getColumnClass(column), { 'clipr-sortable-col': isDateSortColumn(column), 'clipr-sort-active-col': isDateSortActive(section.key, column) }]"
                  >
                    <button
                      v-if="isDateSortColumn(column)"
                      class="btn btn-link clipr-sort-btn"
                      :title="`Ordenar por ${column}`"
                      :aria-label="`Ordenar por ${column}`"
                      @click="toggleDateSort(section.key, column)"
                    >
                      {{ column }}
                      <span class="clipr-sort-indicator">{{ getSortIndicator(section.key, column) }}</span>
                    </button>
                    <span v-else>{{ column }}</span>
                  </th>
                </tr>
                <tr class="clipr-filter-row">
                  <th v-for="column in section.columns" :key="`${section.key}-${column}-filter`" :class="getColumnClass(column)">
                    <select
                      v-if="isSelectFilterColumn(column)"
                      class="form-control form-control-sm clipr-filter-input clipr-filter-select"
                      :value="getColumnFilter(section.key, column)"
                      @change="setColumnFilter(section.key, column, $event.target.value)"
                    >
                      <option value="">Todos</option>
                      <option
                        v-for="opt in getSelectFilterOptions(section.apiRows, column)"
                        :key="`${section.key}-${column}-opt-${opt.value}`"
                        :value="opt.value"
                      >
                        {{ opt.label }}
                      </option>
                    </select>
                    <input
                      v-else
                      class="form-control form-control-sm clipr-filter-input"
                      type="text"
                      :value="getColumnFilter(section.key, column)"
                      :placeholder="`Filtrar ${column}`"
                      @input="setColumnFilter(section.key, column, $event.target.value)"
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="section.filteredRows.length === 0" class="clipr-no-results-row">
                  <td :colspan="section.columns.length">
                    <div class="clipr-no-results-message">
                      <i class="ti-search mr-2"></i>
                      No hay resultados con los filtros actuales.
                    </div>
                  </td>
                </tr>
                <tr v-for="(row, rowIndex) in section.filteredRows" :key="`${section.key}-${rowIndex}`">
                  <td v-for="column in section.columns" :key="`${section.key}-${rowIndex}-${column}`" :class="getColumnClass(column)">
                    <span class="clipr-cell-content" :title="getCellTitle(row[column], column)">
                      {{ formatCell(row[column], column) }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </card>
  </div>
</template>

<script>
import { Card } from "@/components";
import { supabase } from "@/supabase";
import { useCurrentUserStore } from "@/stores/currentUser";

export default {
  name: "CliprPurchases",
  components: { Card },
  data() {
    return {
      loading: false,
      errorMessage: "",
      limit: 100,
      showWelcomeBonus: false,
      tables: {
        clipr_billing_purchases: [],
      },
      columnFilters: {
        clipr_billing_purchases: {
          isLive: "true",
        },
      },
      sortState: {
        clipr_billing_purchases: {
          column: "",
          direction: "",
        },
      },
    };
  },
  computed: {
    tableSections() {
      const apiRows = this.tables.clipr_billing_purchases || [];
      const poolRows = this.applyWelcomeBonusVisibility(apiRows);
      const purchasesColumns = this.resolveColumns(apiRows);
      const filteredRows = this.applyColumnFilters("clipr_billing_purchases", poolRows, purchasesColumns);
      const sortedRows = this.applySectionSort("clipr_billing_purchases", filteredRows);
      return [
        {
          key: "clipr_billing_purchases",
          label: "clipr_billing_purchases",
          apiRows,
          poolRows,
          filteredRows: sortedRows,
          columns: purchasesColumns,
          hasActiveFilters: this.hasActiveFilters("clipr_billing_purchases", purchasesColumns),
        },
      ];
    },
    isTwitchUser() {
      return useCurrentUserStore().isTwitchUser;
    },
  },
  async mounted() {
    await this.loadTables();
  },
  methods: {
    getApiUrl() {
      return process.env.VUE_APP_API_URL || "http://localhost:8000";
    },
    async getSessionOrFail() {
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();
      if (error || !session || !session.access_token) {
        throw new Error("Debes iniciar sesión para ver esta sección.");
      }
      return session;
    },
    resolveColumns(rows) {
      if (!Array.isArray(rows) || rows.length === 0) return [];
      const keys = Object.keys(rows[0]);
      const head = ["created_at", "pack_code", "user_email"];
      const ordered = [];
      for (const k of head) {
        if (keys.includes(k)) ordered.push(k);
      }
      const rest = keys.filter((k) => !ordered.includes(k));
      return [...ordered, ...rest];
    },
    applyWelcomeBonusVisibility(rows) {
      if (this.showWelcomeBonus) return rows;
      return rows.filter((row) => String(row?.pack_code || "") !== "welcome_bonus");
    },
    stringifyCellForFilter(value) {
      if (value === null || value === undefined) return "";
      if (typeof value === "object") return JSON.stringify(value);
      return String(value);
    },
    normalizeFilterValue(value) {
      return this.stringifyCellForFilter(value).trim();
    },
    isSelectFilterColumn(column) {
      return ["purchase_type", "pack_code", "provider_subscription_id", "status", "currency", "isLive"].includes(column);
    },
    isDateSortColumn(column) {
      return ["created_at", "updated_at", "confirmed_at", "failed_at"].includes(column);
    },
    isDateColumn(column) {
      return ["created_at", "updated_at", "confirmed_at", "failed_at"].includes(column);
    },
    formatDateCell(value) {
      const raw = String(value || "");
      if (!raw) return "";
      if (raw.includes("T")) return raw.split("T")[0];
      return raw;
    },
    getSortIndicator(sectionKey, column) {
      const state = this.sortState?.[sectionKey] || {};
      if (state.column !== column || !state.direction) return "↕";
      return state.direction === "asc" ? "↑" : "↓";
    },
    isDateSortActive(sectionKey, column) {
      const state = this.sortState?.[sectionKey] || {};
      return state.column === column && !!state.direction;
    },
    toggleDateSort(sectionKey, column) {
      const current = this.sortState?.[sectionKey] || { column: "", direction: "" };
      let nextDirection = "asc";

      if (current.column === column && current.direction === "asc") {
        nextDirection = "desc";
      } else if (current.column === column && current.direction === "desc") {
        nextDirection = "";
      }

      this.$set(this.sortState, sectionKey, {
        column: nextDirection ? column : "",
        direction: nextDirection,
      });
    },
    toTimestamp(value) {
      if (!value) return null;
      const ts = Date.parse(String(value));
      return Number.isNaN(ts) ? null : ts;
    },
    applySectionSort(sectionKey, rows) {
      const state = this.sortState?.[sectionKey] || {};
      if (!state.column || !state.direction || !this.isDateSortColumn(state.column)) return rows;

      const directionFactor = state.direction === "asc" ? 1 : -1;
      const sorted = [...rows];
      sorted.sort((a, b) => {
        const aTs = this.toTimestamp(a?.[state.column]);
        const bTs = this.toTimestamp(b?.[state.column]);

        if (aTs === null && bTs === null) return 0;
        if (aTs === null) return 1;
        if (bTs === null) return -1;
        if (aTs === bTs) return 0;
        return aTs < bTs ? -1 * directionFactor : 1 * directionFactor;
      });

      return sorted;
    },
    getSelectFilterOptions(rows, column) {
      if (column === "isLive") {
        return [
          { value: "true", label: "true" },
          { value: "false", label: "false" },
        ];
      }

      const seen = new Set();
      const options = [];

      rows.forEach((row) => {
        const normalized = this.normalizeFilterValue(row[column]);
        const token = normalized === "" ? "__EMPTY__" : normalized;
        if (seen.has(token)) return;
        seen.add(token);
        options.push({
          value: token,
          label: normalized === "" ? "(Vacío)" : normalized,
        });
      });

      return options.sort((a, b) => a.label.localeCompare(b.label, "es", { sensitivity: "base" }));
    },
    applyColumnFilters(sectionKey, rows, columns) {
      const sectionFilters = this.columnFilters[sectionKey] || {};
      const activeColumns = columns.filter((column) => String(sectionFilters[column] || "").trim() !== "");
      if (activeColumns.length === 0) return rows;

      return rows.filter((row) => {
        return activeColumns.every((column) => {
          const rawQuery = String(sectionFilters[column] || "").trim();
          const normalizedCell = this.normalizeFilterValue(row[column]);

          if (this.isSelectFilterColumn(column)) {
            if (rawQuery === "__EMPTY__") return normalizedCell === "";
            if (column === "isLive") return normalizedCell.toLowerCase() === rawQuery.toLowerCase();
            return normalizedCell === rawQuery;
          }

          return normalizedCell.toLowerCase().includes(rawQuery.toLowerCase());
        });
      });
    },
    hasActiveFilters(sectionKey, columns) {
      const sectionFilters = this.columnFilters[sectionKey] || {};
      return columns.some((column) => String(sectionFilters[column] || "").trim() !== "");
    },
    getColumnFilter(sectionKey, column) {
      return this.columnFilters?.[sectionKey]?.[column] || "";
    },
    setColumnFilter(sectionKey, column, value) {
      if (!this.columnFilters[sectionKey]) {
        this.$set(this.columnFilters, sectionKey, {});
      }
      this.$set(this.columnFilters[sectionKey], column, value);
    },
    clearSectionFilters(sectionKey) {
      if (!this.columnFilters[sectionKey]) return;
      const sectionFilters = this.columnFilters[sectionKey];
      Object.keys(sectionFilters).forEach((column) => {
        this.$set(sectionFilters, column, this.getDefaultFilterValue(column));
      });
    },
    getDefaultFilterValue(column) {
      if (column === "isLive") return "true";
      return "";
    },
    syncSectionFilters(sectionKey, columns) {
      const prev = this.columnFilters[sectionKey] || {};
      const next = {};
      columns.forEach((column) => {
        if (Object.prototype.hasOwnProperty.call(prev, column)) {
          next[column] = prev[column];
        } else {
          next[column] = this.getDefaultFilterValue(column);
        }
      });
      this.$set(this.columnFilters, sectionKey, next);
    },
    syncSectionSort(sectionKey, columns) {
      const current = this.sortState?.[sectionKey] || { column: "", direction: "" };
      if (!current.column) return;
      if (!columns.includes(current.column) || !this.isDateSortColumn(current.column)) {
        this.$set(this.sortState, sectionKey, { column: "", direction: "" });
      }
    },
    getColumnClass(column) {
      const longCols = ["reference_id", "user_id", "user_email", "provider_checkout_id", "provider_order_id", "provider_subscription_id", "webhook_id", "event_hash", "provider_event_id"];
      const jsonCols = ["metadata", "payload"];
      if (longCols.includes(column)) return "clipr-col-id";
      if (jsonCols.includes(column)) return "clipr-col-json";
      return "";
    },
    getCellTitle(value, column) {
      if (value === null || value === undefined) return "";
      const str = typeof value === "object" ? JSON.stringify(value) : String(value);
      return str.length > 80 ? str : "";
    },
    formatCell(value, column) {
      if (value === null || value === undefined) return "";
      if (this.isDateColumn(column)) return this.formatDateCell(value);
      let str;
      if (typeof value === "object") {
        str = JSON.stringify(value);
      } else {
        str = String(value);
      }
      let maxLen = 36;
      if (["metadata", "payload"].includes(column)) maxLen = 120;
      else if (column === "user_email") maxLen = 96;
      if (str.length > maxLen) return str.slice(0, maxLen) + "…";
      return str;
    },
    async loadTables() {
      this.loading = true;
      this.errorMessage = "";
      try {
        const session = await this.getSessionOrFail();
        const response = await fetch(`${this.getApiUrl()}/momo/ecartpay/admin/purchases?limit=${this.limit}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        });

        if (!response.ok) {
          const message = await response.text();
          throw new Error(message || "No se pudieron cargar las tablas.");
        }

        const payload = await response.json();
        this.tables = {
          clipr_billing_purchases: payload?.tables?.clipr_billing_purchases || [],
        };
        const purchasesColumns = this.resolveColumns(this.tables.clipr_billing_purchases);
        this.syncSectionFilters("clipr_billing_purchases", purchasesColumns);
        this.syncSectionSort("clipr_billing_purchases", purchasesColumns);
      } catch (error) {
        this.errorMessage = error?.message || "Error inesperado cargando tablas.";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.clipr-purchases-page {
  padding: 8px 0;
}

.limit-select {
  width: 88px;
}

.clipr-purchases-body {
  padding: 0 0.5rem;
}

.clipr-table-section {
  margin-bottom: 1.5rem;
}

.clipr-table-header {
  padding: 12px 16px;
  margin-bottom: 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  background: rgba(0, 0, 0, 0.02);
  border-radius: 8px 8px 0 0;
}

.clipr-table-title {
  font-size: 0.875rem;
  color: #475569;
}

.clipr-table-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 0 0 8px 8px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-top: none;
}

.clipr-table-empty i {
  font-size: 2rem;
  color: #94a3b8;
  margin-bottom: 8px;
}

.clipr-table-empty p {
  margin: 0;
  color: #64748b;
  font-size: 0.9rem;
}

.clipr-table-responsive {
  overflow: auto;
  max-height: 68vh;
  -webkit-overflow-scrolling: touch;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-top: none;
  border-radius: 0 0 8px 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.clipr-data-table {
  margin-bottom: 0 !important;
  min-width: 800px;
}

.clipr-data-table thead th {
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  color: #475569;
  font-weight: 600;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 12px 14px;
  border-bottom: 2px solid #e2e8f0;
  white-space: nowrap;
  position: sticky;
  top: 0;
  z-index: 3;
}

.clipr-filter-row th {
  padding: 8px 10px;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 42px;
  z-index: 3;
  background: #f8fafc;
}

.clipr-filter-input {
  min-width: 140px;
  font-size: 0.75rem;
  padding: 0.25rem 0.4rem;
}

.clipr-filter-select {
  cursor: pointer;
}

.clipr-sort-btn {
  padding: 0;
  margin: 0;
  border: 0;
  color: inherit;
  font-weight: 600;
  font-size: inherit;
  line-height: 1.1;
  text-transform: inherit;
  letter-spacing: inherit;
  text-decoration: none !important;
  white-space: nowrap;
  cursor: pointer;
  border-radius: 6px;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.clipr-sort-btn:hover {
  background: rgba(71, 85, 105, 0.08);
}

.clipr-sort-indicator {
  display: inline-block;
  min-width: 12px;
  margin-left: 4px;
  color: #94a3b8;
  font-weight: 700;
}

.clipr-sortable-col {
  cursor: pointer;
}

.clipr-sort-active-col {
  box-shadow: inset 0 -2px 0 #3b82f6;
}

.clipr-sort-active-col .clipr-sort-indicator {
  color: #3b82f6;
}

.clipr-data-table tbody td {
  padding: 12px 14px;
  vertical-align: middle;
  border-bottom: 1px solid #f1f5f9;
  font-size: 0.8rem;
}

.clipr-data-table tbody tr:hover {
  background-color: #f8fafc;
}

.clipr-data-table tbody tr:last-child td {
  border-bottom: none;
}

.clipr-no-results-row td {
  background: #fff;
}

.clipr-no-results-message {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  color: #64748b;
  font-size: 0.85rem;
  padding: 16px 8px;
}

.clipr-cell-content {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.clipr-col-id .clipr-cell-content {
  max-width: 180px;
  font-family: ui-monospace, "SF Mono", Monaco, monospace;
  font-size: 0.75rem;
}

.clipr-col-json .clipr-cell-content {
  max-width: 200px;
  font-family: ui-monospace, "SF Mono", Monaco, monospace;
  font-size: 0.75rem;
}

/* Tema Twitch (oscuro) */
.clipr-purchases-page.twitch-theme :deep(.card) {
  background: var(--clipr-bg-card) !important;
  border: 1px solid var(--clipr-border-color) !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5), 0 0 30px var(--clipr-purple-glow) !important;
  color: var(--clipr-text-primary) !important;
}

.clipr-purchases-page.twitch-theme :deep(.card-header),
.clipr-purchases-page.twitch-theme :deep(.card-title) {
  color: var(--clipr-text-primary) !important;
}

.clipr-purchases-page.twitch-theme :deep(.card-subtitle),
.clipr-purchases-page.twitch-theme .text-muted {
  color: var(--clipr-text-secondary) !important;
}

.clipr-purchases-page.twitch-theme :deep(.btn-primary) {
  background: var(--clipr-purple-gradient) !important;
  box-shadow: 0 8px 30px var(--clipr-purple-glow) !important;
  border: none !important;
  color: white !important;
}

.clipr-purchases-page.twitch-theme :deep(.btn-primary:hover) {
  background: linear-gradient(135deg, #a855f7 0%, #d946ef 100%) !important;
  box-shadow: 0 12px 40px rgba(168, 85, 247, 0.5) !important;
}

.clipr-purchases-page.twitch-theme .clipr-table-header {
  background: rgba(168, 85, 247, 0.12) !important;
  border-color: var(--clipr-border-color) !important;
}

.clipr-purchases-page.twitch-theme .clipr-table-title {
  color: var(--clipr-purple-light) !important;
}

.clipr-purchases-page.twitch-theme .clipr-table-empty {
  background: var(--clipr-bg-elevated) !important;
  border-color: var(--clipr-border-color) !important;
  color: var(--clipr-text-secondary) !important;
}

.clipr-purchases-page.twitch-theme .clipr-table-responsive {
  border-color: var(--clipr-border-color) !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5), 0 0 20px var(--clipr-purple-glow) !important;
}

.clipr-purchases-page.twitch-theme :deep(.clipr-data-table thead th) {
  background: rgba(168, 85, 247, 0.15) !important;
  color: var(--clipr-purple-light) !important;
  border-bottom-color: var(--clipr-border-purple) !important;
}

.clipr-purchases-page.twitch-theme :deep(.clipr-filter-row th) {
  background: rgba(34, 17, 56, 0.95) !important;
  border-bottom-color: var(--clipr-border-purple) !important;
}

.clipr-purchases-page.twitch-theme :deep(.clipr-filter-input) {
  background: var(--clipr-bg-elevated) !important;
  border-color: var(--clipr-border-color) !important;
  color: var(--clipr-text-primary) !important;
}

.clipr-purchases-page.twitch-theme :deep(.clipr-sort-btn),
.clipr-purchases-page.twitch-theme :deep(.clipr-sort-indicator) {
  color: var(--clipr-purple-light) !important;
}

.clipr-purchases-page.twitch-theme :deep(.clipr-sort-btn:hover) {
  background: rgba(168, 85, 247, 0.16) !important;
}

.clipr-purchases-page.twitch-theme :deep(.clipr-sort-active-col) {
  box-shadow: inset 0 -2px 0 var(--clipr-purple-light) !important;
}

.clipr-purchases-page.twitch-theme :deep(.clipr-data-table tbody td) {
  color: var(--clipr-text-primary) !important;
  background: var(--clipr-bg-elevated) !important;
  border-color: var(--clipr-border-color) !important;
}

.clipr-purchases-page.twitch-theme :deep(.clipr-no-results-message) {
  color: var(--clipr-text-secondary) !important;
}

.clipr-purchases-page.twitch-theme :deep(.clipr-data-table tbody tr:hover) {
  background: rgba(168, 85, 247, 0.08) !important;
}

.clipr-purchases-page.twitch-theme :deep(.badge-secondary) {
  background: rgba(168, 85, 247, 0.25) !important;
  color: var(--clipr-purple-light) !important;
  border: 1px solid var(--clipr-border-purple) !important;
}
</style>
